import React from 'react';
import { ArrowRightIcon, DownloadIcon } from 'lucide-react';
import { useEnquiry } from '../../contexts/EnquiryContext';
import { industries } from '../../data/industries';
import { images } from '../../data/images';
import { contact } from '../../data/site';
import { Button } from '../ui/Button';
import { DotMark } from '../ui/DotMark';
import { HeroLeadForm } from '../forms/HeroLeadForm';
export function Hero() {
  const {
    selectIndustry
  } = useEnquiry();
  return <section id="top" aria-labelledby="hero-title" className="relative overflow-hidden bg-afb-night text-white" data-id="element-321" data-mp="ms7olrt041uj">
      <div aria-hidden="true" className="dot-field absolute inset-0" data-id="element-322" data-mp="vmm3ht9mdad4" />
      <div className="relative mx-auto max-w-page px-5 pb-12 pt-12 lg:px-8 lg:pb-16 lg:pt-16" data-id="element-323" data-mp="vofc3e3sm4nr">
        <div className="grid items-center gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-14" data-id="element-324" data-mp="10njki2iw7yd1">
          <div data-id="element-325" data-mp="15md0c1lnk1gd">
            <p className="flex items-center gap-2.5 text-sm text-white/70" data-id="element-326" data-mp="15ip4gvsqj2yh">
              <DotMark data-id="element-327" data-mp="1ak4teyy19tm5" />
              Perth-based · Australia-wide
            </p>
            <h1 id="hero-title" className="mt-6 font-sora text-[2.5rem] font-semibold leading-[1.04] tracking-[-0.03em] sm:text-[3.25rem] lg:text-[3.9rem]" data-id="element-328" data-mp="9olywcsi04oy">
              Containerised power systems for remote &amp; critical operations.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75" data-id="element-329" data-mp="3ruh0rbnsskh8">
              Fast to deploy, simple to operate, built for Australian conditions. Supports diesel displacement,
              microgrids and resilient backup power.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-330" data-mp="io91h1wyrgjl">
              <Button href="#contact" data-id="element-331" data-mp="imeeli24tecv">
                Request a demo / site assessment
                <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-332" data-mp="emngeta9zvyb" />
              </Button>
              <Button href="#capability-pack" variant="outline-dark" data-id="element-333" data-mp="1b6cxmgnw76x5">
                <DownloadIcon aria-hidden="true" className="h-4 w-4" data-id="element-334" data-mp="16dtobvw8mxy5" />
                Download capability pack
              </Button>
            </div>
            <p className="mt-4 text-sm text-white/60" data-id="element-335" data-mp="30o9u0ip7ud7n">
              Or{' '}
              <a href={contact.phoneHref} className="font-medium text-white underline-offset-4 hover:underline" data-id="element-336" data-mp="35kgys85vlio0">
                call {contact.phoneDisplay}
              </a>{' '}
              ·{' '}
              <a href={contact.emailHref} className="font-medium text-white underline-offset-4 hover:underline" data-id="element-337" data-mp="2vte1zijzbpuj">
                email {contact.email}
              </a>
            </p>

            <div className="mt-10" data-id="element-338" data-mp="2mvafaix9cn15">
              <p id="sector-label" className="text-sm font-medium text-white/80" data-id="element-339" data-mp="2ms65n610wz33">
                What are you powering?
              </p>
              <div role="group" aria-labelledby="sector-label" className="no-scrollbar -mx-5 mt-3 flex gap-2 overflow-x-auto px-5 pb-1 lg:mx-0 lg:flex-wrap lg:px-0" data-id="element-340" data-mp="2cvjlykkfoe2n">
                {industries.map(industry => <button key={industry.id} type="button" onClick={() => selectIndustry(industry.id)} className="shrink-0 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-white/85 transition-colors duration-150 hover:border-afb-turquoise/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-afb-turquoise" data-id="element-341" data-mp="1vmr0q5mmtls8">
                    {industry.label}
                  </button>)}
              </div>
            </div>
          </div>

          <div className="relative" data-id="element-342" data-mp="2obfutkp9squh">
            <div className="relative overflow-hidden rounded-card" data-id="element-343" data-mp="2oad7tnv2j8ju">
              <img src={images.hero} alt="Illustrative render of a containerised power system with a solar array at a remote outback site" className="aspect-[4/5] w-full object-cover sm:aspect-[16/11] lg:aspect-[4/5]" data-id="element-344" data-mp="2jc3bvb1is3x6" />
              <span className="absolute right-3 top-3 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white/85" data-id="element-345" data-mp="qfr5p9ocdfnz">
                Illustrative render
              </span>
              <div className="absolute inset-x-4 bottom-4 rounded-card border border-white/10 bg-afb-night/90 p-5 backdrop-blur sm:right-auto sm:w-[320px]" data-id="element-346" data-mp="vgx3hlwuzt4d">
                <p className="text-xs font-medium text-white/60" data-id="element-347" data-mp="15byr2dbhkn4u">All-in-One Power System</p>
                <dl className="mt-3 grid grid-cols-2 gap-4" data-id="element-348" data-mp="7q14a3q2pvn3">
                  <div data-id="element-349" data-mp="7newc2z9te4c">
                    <dt className="sr-only" data-id="element-350" data-mp="cnt4rm4saq2w">Initial setup</dt>
                    <dd className="font-sora text-3xl font-semibold tracking-tight" data-id="element-351" data-mp="cm8dc5drnn99">3–4 hrs</dd>
                    <dd className="mt-1 text-xs text-white/65" data-id="element-352" data-mp="hlcimkxkl0ub">Initial setup</dd>
                  </div>
                  <div data-id="element-353" data-mp="mg8ksdwit4l5">
                    <dt className="sr-only" data-id="element-354" data-mp="3l4e5kfn9icux">Setup thereafter</dt>
                    <dd className="font-sora text-3xl font-semibold tracking-tight text-afb-turquoise" data-id="element-355" data-mp="3l21rweojeono">~30 min</dd>
                    <dd className="mt-1 text-xs text-white/65" data-id="element-356" data-mp="3q0w95c6uw55o">Each time after</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 lg:mt-14" data-id="element-357" data-mp="234eyg8u3pyoa">
          <HeroLeadForm data-id="element-358" data-mp="1y6n4zadt64hz" />
        </div>
      </div>
    </section>;
}